import React, { useState, useEffect } from 'react';
import { Navbar } from './Navbar';
import { getPersonalityTheme } from '../CardFiles/theme-config';
import './SynergyPreview.css';

// ── Chemistry copy for every persona pair ──────────────────────────────────
const CHEMISTRY = {
  'analyst-analyst':   { headline: 'Two Architects of Truth',     sub: 'When two analytical minds unite, ideas become blueprints that actually get built.' },
  'analyst-empath':    { headline: 'Logic Meets Soul',            sub: 'One maps the stars. The other feels them. Together — an unstoppable force.' },
  'analyst-visionary': { headline: 'Precision + Imagination',     sub: 'The blueprint and the dream finally in one room.' },
  'analyst-guardian':  { headline: 'Strategy + Stability',        sub: 'A structure built to last. Your skills interlock perfectly.' },
  'analyst-explorer':  { headline: 'Order Meets Adventure',       sub: 'One builds the map. The other dares to go completely off-road.' },
  'empath-analyst':    { headline: 'Soul Meets Logic',            sub: 'One feels deeply. The other decodes the why. Pure alchemy.' },
  'empath-empath':     { headline: 'Pure Resonance',              sub: 'Two hearts tuned to the same frequency. Rare and genuinely powerful.' },
  'empath-visionary':  { headline: 'Heart + Vision',              sub: 'Feeling and imagination in perfect orbit. You build worlds that matter.' },
  'empath-guardian':   { headline: 'Care + Commitment',           sub: 'A bond forged from deep trust and loyalty that weathers anything.' },
  'empath-explorer':   { headline: 'Warmth Meets Wanderlust',     sub: 'They seek the world. You make every place feel like home.' },
  'visionary-analyst': { headline: 'Dreams + Data',               sub: 'The bold idea and the sharp execution finally meet their match.' },
  'visionary-empath':  { headline: 'Vision + Heart',              sub: 'Together you build worlds that actually change things.' },
  'visionary-visionary':{ headline: 'Double Vision',              sub: 'Two dreamers collide. Expect the extraordinary.' },
  'visionary-guardian':{ headline: 'Stars + Gravity',             sub: 'The visionary needs a ground. The guardian provides it.' },
  'visionary-explorer':{ headline: 'Boundless Horizons',          sub: 'Two creative forces. Nothing is holding you back now.' },
  'guardian-analyst':  { headline: 'Foundation + Framework',      sub: 'Rock-solid commitment meets sharp strategic thinking.' },
  'guardian-empath':   { headline: 'Strength + Warmth',           sub: 'Protection with a heart. A rare and powerful combination.' },
  'guardian-visionary':{ headline: 'Roots + Wings',               sub: 'The anchor that lets the dreamer soar higher than ever.' },
  'guardian-guardian': { headline: 'Unshakeable',                 sub: 'Two pillars. A partnership built to weather any storm.' },
  'guardian-explorer': { headline: 'Safe Harbor + Open Sea',      sub: 'One keeps the ship steady. The other charts the new waters.' },
  'explorer-analyst':  { headline: 'Discovery + Clarity',         sub: 'Curiosity with a compass. You\'ll go further than either expected.' },
  'explorer-empath':   { headline: 'Adventure + Depth',           sub: 'Wandering souls who make every place feel like it matters.' },
  'explorer-visionary':{ headline: 'Infinite Horizons',           sub: 'Two creative spirits with absolutely nothing holding them back.' },
  'explorer-guardian': { headline: 'Freedom + Foundation',        sub: 'The explorer needs an anchor. The guardian was made to provide it.' },
  'explorer-explorer': { headline: 'Wild Curiosity',              sub: 'A duo that discovers something new every single day. Endlessly.' },
};

// ── Extract persona data from any analysis format ──────────────────────────
const extractProfile = (data) => {
  if (!data) return null;
  const pCard = data?.cards_data?.personality_card || data?.personality_card || {};
  const persona = pCard?.persona || data?.persona || null;
  const brainType = typeof pCard?.brainType === 'string' ? pCard.brainType : null;
  const oneLiner = pCard?.oneLiner || data?.oneLiner || null;
  const strengths = (pCard?.coreStrengths || []).slice(0, 2).map(s => typeof s === 'object' ? (s.label || s.name) : s);
  const traits = (pCard?.keyInsights || []).slice(0, 2).map(t => typeof t === 'object' ? (t.insight || t.label) : t);
  const badge = strengths[0] || traits[0] || brainType || null;

  // Fallback persona from brain balance when no handwriting card
  const fallbackPersona = (() => {
    const l = data?.left_percentage || data?.left_percent || 50;
    const r = data?.right_percentage || data?.right_percent || 50;
    if (Math.abs(l - r) < 10) return 'guardian';
    return l > r ? 'analyst' : 'visionary';
  })();

  const resolvedPersona = persona || fallbackPersona;
  const theme = getPersonalityTheme(resolvedPersona);

  return { persona: resolvedPersona, brainType, oneLiner, badge, theme };
};

// ── Profile card shown in the preview ─────────────────────────────────────
const ProfileChip = ({ name, profile, side, revealed }) => {
  const theme = profile?.theme || getPersonalityTheme('analyst');
  const animClass = side === 'left' ? 'sp-slide-left' : 'sp-slide-right';
  const initial = (name || '?').charAt(0).toUpperCase();

  return (
    <div className={`sp-chip ${animClass} ${revealed ? 'sp-chip--in' : ''}`}>
      {/* Glow ring behind the chip */}
      <div className="sp-chip-glow" style={{ background: `radial-gradient(circle, ${theme.primary}50 0%, transparent 70%)` }} />

      {/* Avatar circle */}
      <div className="sp-avatar" style={{ background: `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})`, boxShadow: `0 0 24px ${theme.primary}60` }}>
        <span className="sp-avatar-emoji">{theme.icon}</span>
        <span className="sp-avatar-initial">{initial}</span>
      </div>

      {/* Name */}
      <p className="sp-chip-name">{name || 'Mystery Match'}</p>

      {/* Persona badge */}
      <div className="sp-persona-badge" style={{ background: `${theme.primary}22`, border: `1px solid ${theme.primary}55`, color: theme.accent }}>
        {theme.name}
      </div>

      {/* Trait chip */}
      {profile?.badge && (
        <div className="sp-trait-chip" style={{ color: 'rgba(255,255,255,0.65)' }}>
          {profile.badge}
        </div>
      )}
    </div>
  );
};

// ── Main component ─────────────────────────────────────────────────────────
export const SynergyPreview = ({ user, analysisResult, connection, onReveal, onBack, onSignInClick, onGoHome }) => {
  const [revealed, setRevealed] = useState(false);
  const [chemIn, setChemIn] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setRevealed(true), 80);
    const t2 = setTimeout(() => setChemIn(true), 600);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  // ── Resolve both profiles ──────────────────────────────────────────────
  const myName = user?.name || user?.fullName || user?.firstName || 'You';
  const myProfile = extractProfile(analysisResult);

  const getOtherRaw = () => {
    try {
      const raw = connection?._perspective === 'received'
        ? connection?.inviter_test_results
        : connection?.invited_test_results;
      return typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch { return null; }
  };
  const otherRaw = getOtherRaw();
  const otherProfile = extractProfile(otherRaw);
  const otherName = (() => {
    if (connection?._connected_name) return connection._connected_name;
    if (connection?.invited_email && !['link-invite', 'link-invite-accepted'].includes(connection.invited_email)) {
      return connection.invited_email.split('@')[0];
    }
    return connection?._perspective === 'received' ? 'Your Match' : 'Linked Profile';
  })();

  // ── Chemistry copy ─────────────────────────────────────────────────────
  const chemKey = `${myProfile?.persona || 'analyst'}-${otherProfile?.persona || 'visionary'}`;
  const chem = CHEMISTRY[chemKey] || { headline: 'A Unique Connection', sub: 'Two distinct minds. One powerful synergy. Let\'s see what unfolds.' };

  // ── Background orbs use both persona colors ────────────────────────────
  const c1 = myProfile?.theme?.primary || '#8b5cf6';
  const c2 = otherProfile?.theme?.primary || '#ec4899';

  return (
    <div className="sp-page base-background">
      {/* Animated ambient background */}
      <div className="sp-ambient" aria-hidden="true">
        <div className="sp-orb sp-orb-1" style={{ background: `radial-gradient(circle, ${c1}45 0%, transparent 70%)` }} />
        <div className="sp-orb sp-orb-2" style={{ background: `radial-gradient(circle, ${c2}35 0%, transparent 70%)` }} />
        <div className="sp-orb sp-orb-3" style={{ background: `radial-gradient(circle, rgba(168,85,247,0.2) 0%, transparent 70%)` }} />
      </div>

      <Navbar currentUser={user} onSignInClick={onSignInClick} onGoHome={onGoHome} />

      <div className="sp-container">

        {/* ── Header ── */}
        <div className={`sp-header ${revealed ? 'sp-fade-up' : ''}`}>
          <div className="sp-eyebrow">
            <span className="sp-eyebrow-dot" style={{ background: c1 }} />
            Synergy Match Unlocked
            <span className="sp-eyebrow-dot" style={{ background: c2 }} />
          </div>
          <h1 className="sp-title">
            Here's who you're <br />
            <span className="sp-title-gradient" style={{ backgroundImage: `linear-gradient(135deg, ${c1}, ${c2})` }}>
              about to discover
            </span>
          </h1>
          <p className="sp-subtitle">Two personalities. One AI-powered compatibility read.</p>
        </div>

        {/* ── Dual profile chips ── */}
        <div className="sp-chips-row">
          <ProfileChip name={myName} profile={myProfile} side="left" revealed={revealed} />

          {/* Connector */}
          <div className={`sp-connector ${revealed ? 'sp-connector--in' : ''}`}>
            <div className="sp-connector-ring sp-ring-1" style={{ borderColor: `${c1}50` }} />
            <div className="sp-connector-ring sp-ring-2" style={{ borderColor: `${c2}40` }} />
            <div className="sp-connector-core" style={{ background: `linear-gradient(135deg, ${c1}, ${c2})` }}>
              <span className="sp-connector-icon">⚡</span>
            </div>
          </div>

          <ProfileChip name={otherName} profile={otherProfile} side="right" revealed={revealed} />
        </div>

        {/* ── Chemistry card ── */}
        <div className={`sp-chemistry-card ${chemIn ? 'sp-chem--in' : ''}`}
          style={{ border: `1px solid ${c1}30`, background: `linear-gradient(135deg, ${c1}12, ${c2}12)` }}>
          <div className="sp-chem-label">Your Synergy Chemistry</div>
          <h2 className="sp-chem-headline" style={{ backgroundImage: `linear-gradient(135deg, ${c1}, ${c2})` }}>
            {chem.headline}
          </h2>
          <p className="sp-chem-sub">{chem.sub}</p>

          <div className="sp-chem-dims">
            {['Professional Synergy', 'Friendship Resonance', 'Romantic Harmony', 'Co-Founder Fit'].map((d, i) => (
              <div key={i} className="sp-dim-chip">
                <div className="sp-dim-dot" style={{ background: [c1, c2, c1, c2][i], opacity: 0.8 }} />
                {d}
              </div>
            ))}
          </div>
        </div>

        {/* ── CTA ── */}
        <div className={`sp-cta-wrap ${chemIn ? 'sp-cta--in' : ''}`}>
          <button
            className="sp-cta-btn"
            style={{ background: `linear-gradient(135deg, ${c1}, ${c2})`, boxShadow: `0 8px 32px ${c1}55` }}
            onClick={onReveal}
          >
            <span>Reveal Your Synergy</span>
            <span className="sp-cta-arrow">→</span>
          </button>
          <p className="sp-cta-meta">AI-powered · 4 dimensions · Instant results</p>
        </div>

      </div>
    </div>
  );
};

export default SynergyPreview;
